import {
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  fetchPlayerStats,
  type PlayerStatsResponse,
  type FetchPlayerStatsParams,
} from '@/lib/network';

const PLAYER_STATS_STALE_TIME_MS = 5 * 60 * 1000;
const PLAYER_STATS_GC_TIME_MS = 10 * 60 * 1000;

export type UseFetchPlayerStatsOptions<TData = InfiniteData<PlayerStatsResponse>> = Omit<
  UseInfiniteQueryOptions<PlayerStatsResponse, Error, TData>,
  'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
>;

export const useFetchPlayerStats = <TData = InfiniteData<PlayerStatsResponse>>(
  filters: FetchPlayerStatsParams = {},
  options?: UseFetchPlayerStatsOptions<TData>
) => {
  return useInfiniteQuery<PlayerStatsResponse, Error, TData>({
    queryKey: ['players', 'stats', filters.tournamentCalendarId, filters.type, filters],
    queryFn: async ({ pageParam }) => {
      return fetchPlayerStats({ ...filters, page: pageParam as number, limit: 100 });
    },
    getNextPageParam: lastPage => {
      const { page, totalPages } = lastPage.data.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: PLAYER_STATS_STALE_TIME_MS,
    gcTime: PLAYER_STATS_GC_TIME_MS,
    ...options,
  });
};
